<template>
  <main-layout>
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1 class="text-h4 mb-4">Admin Panel</h1>
          
          <!-- Dashboard Statistics Section -->
          <v-card class="mb-4">
            <v-card-title>
              <v-icon left>mdi-chart-box</v-icon>
              Dashboard Statistics
              <v-spacer></v-spacer>
              <v-btn color="primary" small @click="loadDashboard" :loading="loadingDashboard">
                <v-icon left small>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-progress-linear v-if="loadingDashboard" indeterminate></v-progress-linear>
              
              <v-row>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 primary--text">{{ dashboard.total_users || 0 }}</div>
                      <div class="text-subtitle-1">Total Users</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 success--text">{{ dashboard.total_accounts || 0 }}</div>
                      <div class="text-subtitle-1">Total Accounts</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 warning--text">{{ dashboard.total_conversion_batches || 0 }}</div>
                      <div class="text-subtitle-1">Conversion Batches</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined>
                    <v-card-text class="text-center">
                      <div class="text-h4 info--text">{{ dashboard.total_file_conversions || 0 }}</div>
                      <div class="text-subtitle-1">File Conversions</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              
              <!-- Conversion Status Stats -->
              <v-row v-if="dashboard.conversion_stats" class="mt-4">
                <v-col cols="12">
                  <h3 class="text-h6 mb-2">Conversion Status</h3>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined color="orange lighten-4">
                    <v-card-text class="text-center">
                      <div class="text-h5 orange--text text--darken-2">{{ dashboard.conversion_stats.pending || 0 }}</div>
                      <div class="text-subtitle-2">Pending</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined color="blue lighten-4">
                    <v-card-text class="text-center">
                      <div class="text-h5 blue--text text--darken-2">{{ dashboard.conversion_stats.processing || 0 }}</div>
                      <div class="text-subtitle-2">Processing</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined color="green lighten-4">
                    <v-card-text class="text-center">
                      <div class="text-h5 green--text text--darken-2">{{ dashboard.conversion_stats.completed || 0 }}</div>
                      <div class="text-subtitle-2">Completed</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card outlined color="red lighten-4">
                    <v-card-text class="text-center">
                      <div class="text-h5 red--text text--darken-2">{{ dashboard.conversion_stats.failed || 0 }}</div>
                      <div class="text-subtitle-2">Failed</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          
          <!-- Billing Reports Section -->
          <v-card class="mb-4">
            <v-card-title>
              <v-icon left>mdi-file-chart</v-icon>
              Billing Reports
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="reportStartDate"
                        label="Start Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="reportStartDate"
                      @input="startDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="reportEndDate"
                        label="End Date"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="reportEndDate"
                      @input="endDateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              
              <v-row class="mt-4">
                <v-col cols="12">
                  <v-btn 
                    color="primary" 
                    @click="generateBillingReport" 
                    :loading="generatingReport"
                    :disabled="!reportStartDate || !reportEndDate"
                  >
                    <v-icon left>mdi-chart-line</v-icon>
                    Generate Report
                  </v-btn>
                  <v-btn 
                    color="success" 
                    @click="downloadDetailCSV" 
                    :disabled="!reportStartDate || !reportEndDate"
                    class="ml-2"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Download Detail CSV
                  </v-btn>
                  <v-btn 
                    color="blue" 
                    @click="downloadSummaryCSV" 
                    :disabled="!reportStartDate || !reportEndDate"
                    class="ml-2"
                  >
                    <v-icon left>mdi-download</v-icon>
                    Download Summary CSV
                  </v-btn>
                </v-col>
              </v-row>
              
              <!-- Report Results -->
              <div v-if="billingReport">
                <v-divider class="mb-4"></v-divider>
                <h3 class="text-h6 mb-4">Report Results ({{ billingReport.period.start_date }} to {{ billingReport.period.end_date }})</h3>
                
                <!-- Summary Stats -->
                <v-row class="mb-4">
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-text class="text-center">
                        <div class="text-h4 primary--text">{{ billingReport.summary.total_conversions || 0 }}</div>
                        <div class="text-subtitle-1">Total Conversions</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-text class="text-center">
                        <div class="text-h4 success--text">{{ billingReport.summary.total_completed || 0 }}</div>
                        <div class="text-subtitle-1">Completed</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-text class="text-center">
                        <div class="text-h4 info--text">{{ billingReport.summary.unique_customers || 0 }}</div>
                        <div class="text-subtitle-1">Unique Companies</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                
                <!-- Summary Data Table -->
                <v-card v-if="billingReport.summary_data && billingReport.summary_data.length > 0" class="mb-4">
                  <v-card-title>
                    <v-icon left>mdi-table</v-icon>
                    Summary Report
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="summaryHeaders"
                      :items="billingReport.summary_data"
                      :items-per-page="10"
                      class="elevation-1"
                    >
                    </v-data-table>
                  </v-card-text>
                </v-card>
                
                <!-- Detail Data Table -->
                <v-card v-if="billingReport.detail_data && billingReport.detail_data.length > 0">
                  <v-card-title>
                    <v-icon left>mdi-table-large</v-icon>
                    Detailed Report
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="detailHeaders"
                      :items="billingReport.detail_data"
                      :items-per-page="10"
                      class="elevation-1"
                    >
                      <template v-slot:item.Status="{ item }">
                        <v-chip
                          :color="item.Status === 'completed' ? 'success' : item.Status === 'failed' ? 'error' : 'warning'"
                          text-color="white"
                          small
                        >
                          {{ item.Status }}
                        </v-chip>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </div>
            </v-card-text>
          </v-card>
          
          <!-- Account & User Management Section -->
          <v-card class="mb-4">
            <v-card-title>
              <v-icon left>mdi-office-building</v-icon>
              Account & User Management
              <v-spacer></v-spacer>
              <v-btn color="success" @click="showCreateAccountDialog" class="mr-2">
                <v-icon left>mdi-plus</v-icon>
                Create Account
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-btn color="grey" outlined @click="loadAccountsAndUsers" :loading="loadingAccounts || loadingUsers" class="mb-4">
                <v-icon left>mdi-refresh</v-icon>
                Refresh All Data
              </v-btn>
              
              <!-- Hierarchical Account-User Display -->
              <div v-if="accountsWithUsers.length > 0">
                <v-expansion-panels multiple>
                  <v-expansion-panel v-for="account in accountsWithUsers" :key="account.id">
                    <v-expansion-panel-header>
                      <template v-slot:default>
                        <v-row no-gutters>
                          <v-col cols="4">
                            <strong>{{ account.name }}</strong>
                            <v-chip v-if="account.disabled" color="grey" text-color="white" x-small class="ml-2">
                              Disabled
                            </v-chip>
                          </v-col>
                          <v-col cols="2" class="text--secondary">
                            ID: {{ account.id }}
                          </v-col>
                          <v-col cols="3" class="text--secondary">
                            {{ account.users.length }} {{ account.users.length === 1 ? 'user' : 'users' }}
                          </v-col>
                          <v-col cols="3" class="text--secondary">
                            {{ account.conversion_batches_count || 0 }} conversions
                          </v-col>
                        </v-row>
                      </template>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <!-- Account Details -->
                      <v-card flat class="mb-3">
                        <v-card-subtitle>Account Details</v-card-subtitle>
                        <v-card-text>
                          <v-row>
                            <v-col cols="6">
                              <strong>Source Platforms:</strong> {{ account.source_platforms || 'None specified' }}
                            </v-col>
                            <v-col cols="6">
                              <strong>Destination Platforms:</strong> {{ account.destination_platforms || 'None specified' }}
                            </v-col>
                          </v-row>
                          <v-row>
                            <v-col cols="6">
                              <strong>Created:</strong> {{ formatDate(account.created_at) }}
                            </v-col>
                            <v-col cols="6">
                              <v-btn small outlined @click="editAccount(account)">
                                <v-icon left small>mdi-pencil</v-icon>
                                Edit Account
                              </v-btn>
                              <v-btn
                                v-if="canChangeAccountStatus(account)"
                                small
                                outlined
                                :color="account.disabled ? 'success' : 'error'"
                                class="ml-2"
                                @click="confirmStatusChange('account', account)"
                              >
                                <v-icon left small>{{ account.disabled ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                                {{ account.disabled ? 'Enable Account' : 'Disable Account' }}
                              </v-btn>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                      
                      <!-- Users in this Account -->
                      <v-card flat>
                        <v-card-subtitle>
                          Users in {{ account.name }}
                          <v-btn small color="success" @click="showCreateUserDialog(account.id)" class="ml-2">
                            <v-icon left small>mdi-account-plus</v-icon>
                            Add User to Account
                          </v-btn>
                        </v-card-subtitle>
                        <v-card-text>
                          <v-data-table
                            :headers="userHeadersCompact"
                            :items="account.users"
                            :items-per-page="5"
                            :hide-default-footer="account.users.length <= 5"
                            dense
                          >
                            <template v-slot:item.role="{ item }">
                              <v-chip
                                :color="getRoleColor(item.role)"
                                text-color="white"
                                small
                              >
                                {{ item.role }}
                              </v-chip>
                            </template>
                            <template v-slot:item.disabled="{ item }">
                              <v-chip v-if="item.disabled" color="grey" text-color="white" small>Disabled</v-chip>
                              <v-chip v-else-if="account.disabled" color="grey" outlined small>Account disabled</v-chip>
                              <v-chip v-else color="green" text-color="white" small>Active</v-chip>
                            </template>
                            <template v-slot:item.created_at="{ item }">
                              {{ formatDate(item.created_at) }}
                            </template>
                            <template v-slot:item.actions="{ item }">
                              <v-btn icon small @click="editUser(item)" class="mr-1">
                                <v-icon>mdi-pencil</v-icon>
                              </v-btn>
                              <v-btn icon small @click="resetPassword(item)" color="orange" class="mr-1">
                                <v-icon>mdi-lock-reset</v-icon>
                              </v-btn>
                              <v-btn
                                v-if="canChangeUserStatus(item)"
                                icon
                                small
                                :color="item.disabled ? 'green' : 'red'"
                                class="mr-1"
                                :title="item.disabled ? 'Enable user' : 'Disable user'"
                                @click="confirmStatusChange('user', item)"
                              >
                                <v-icon>{{ item.disabled ? 'mdi-account-check' : 'mdi-account-cancel' }}</v-icon>
                              </v-btn>
                            </template>
                            <template v-slot:no-data>
                              <div class="text-center text--secondary">
                                No users in this account yet.
                              </div>
                            </template>
                          </v-data-table>
                        </v-card-text>
                      </v-card>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
              
              <v-alert v-else-if="!loadingAccounts && !loadingUsers" type="info" class="mt-4">
                No accounts found. Create an account to get started.
              </v-alert>
              
              <v-progress-linear v-if="loadingAccounts || loadingUsers" indeterminate class="mt-4"></v-progress-linear>
            </v-card-text>
          </v-card>
          
          <!-- Recent Activity Section -->
          <v-row v-if="dashboard.recent_users || dashboard.recent_accounts">
            <v-col cols="12" md="6" v-if="dashboard.recent_users">
              <v-card>
                <v-card-title>
                  <v-icon left>mdi-account-clock</v-icon>
                  Recent Users
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item v-for="user in dashboard.recent_users" :key="user.id">
                      <v-list-item-content>
                        <v-list-item-title>{{ user.email }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ user.account_name }} • {{ formatDate(user.created_at) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-chip
                          :color="getRoleColor(user.role)"
                          text-color="white"
                          small
                        >
                          {{ user.role }}
                        </v-chip>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" v-if="dashboard.recent_accounts">
              <v-card>
                <v-card-title>
                  <v-icon left>mdi-office-building</v-icon>
                  Recent Accounts
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item v-for="account in dashboard.recent_accounts" :key="account.id">
                      <v-list-item-content>
                        <v-list-item-title>{{ account.name }}</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ account.users_count }} users • {{ formatDate(account.created_at) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          
          <!-- System Settings Section (Super Admin Only) -->
          <v-card v-if="userRole === 'super_admin'">
            <v-card-title>
              <v-icon left>mdi-cog</v-icon>
              System Settings
            </v-card-title>
            <v-card-text>
              <v-alert type="warning" class="mb-4">
                <strong>Super Admin Only:</strong> These settings affect the entire system.
              </v-alert>
              
              <v-btn color="secondary" outlined>
                <v-icon left>mdi-database</v-icon>
                Database Maintenance
              </v-btn>
              
              <v-btn color="secondary" outlined class="ml-2">
                <v-icon left>mdi-file-export</v-icon>
                Export Data
              </v-btn>
              
              <v-btn color="secondary" outlined class="ml-2">
                <v-icon left>mdi-server</v-icon>
                System Logs
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    
    <!-- User Edit Dialog -->
    <v-dialog v-model="editUserDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit User</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedUser.email"
                  label="Email"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editedUser.role"
                  :items="roleOptions"
                  label="Role"
                  :disabled="userRole !== 'super_admin'"
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeEditDialog">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="saveUser" :disabled="userRole !== 'super_admin'">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Account Edit Dialog -->
    <v-dialog v-model="editAccountDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingAccount ? 'Edit Account' : 'Create Account' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedAccount.name"
                  label="Account Name"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedAccount.source_platforms"
                  label="Source Platforms (comma-separated)"
                  hint="e.g., platform1,platform2,platform3"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedAccount.destination_platforms"
                  label="Destination Platforms (comma-separated)"
                  hint="e.g., platform4,platform5"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeAccountDialog">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="saveAccount">
            {{ editingAccount ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Create User Dialog -->
    <v-dialog v-model="createUserDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Create New User</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="newUser.email"
                  label="Email Address *"
                  type="email"
                  required
                  :rules="emailRules"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="newUser.password"
                  label="Password *"
                  type="password"
                  required
                  :rules="passwordRules"
                  hint="Minimum 6 characters"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="newUser.role"
                  :items="availableRoleOptions"
                  label="Role"
                  :hint="userRole === 'super_admin' ? 'Defaults to \'user\' if not specified' : 'Regular Admins can only create users with \'user\' role'"
                  :disabled="userRole !== 'super_admin'"
                ></v-select>
              </v-col>
              <v-col cols="12" v-if="userRole === 'super_admin'">
                <v-select
                  v-model="newUser.account_id"
                  :items="accountOptions"
                  label="Account *"
                  hint="Super Admin can assign user to any account"
                  item-text="name"
                  item-value="id"
                  required
                >
                  <template v-slot:item="{ item }">
                    <span>{{ item.name }} (ID: {{ item.id }})</span>
                  </template>
                </v-select>
                <v-alert type="info" dense class="mt-2" v-if="accounts.length === 0">
                  Load accounts first to see account options
                </v-alert>
                <v-alert type="success" dense class="mt-2" v-if="newUser.account_id">
                  User will be created in: {{ getAccountName(newUser.account_id) }}
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
          <small>* Required fields</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeCreateUserDialog">
            Cancel
          </v-btn>
          <v-btn 
            color="blue darken-1" 
            text 
            @click="createUser"
            :disabled="!isCreateUserFormValid"
            :loading="creatingUser"
          >
            Create User
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Reset Password Dialog -->
    <v-dialog v-model="resetPasswordDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Reset Password</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-alert type="warning" class="mb-4">
              <strong>Warning:</strong> This will reset the password for {{ selectedUser ? selectedUser.email : '' }}
            </v-alert>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :value="selectedUser ? selectedUser.email : ''"
                  label="User Email"
                  readonly
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="newPassword"
                  label="New Password *"
                  type="password"
                  required
                  :rules="passwordRules"
                  hint="Minimum 6 characters"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
          <small>* Required fields</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeResetPasswordDialog">
            Cancel
          </v-btn>
          <v-btn 
            color="orange darken-1" 
            text 
            @click="confirmPasswordReset"
            :disabled="!newPassword || newPassword.length < 6"
          >
            Reset Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Disable / Enable Confirmation Dialog -->
    <v-dialog v-model="statusDialog" max-width="500px">
      <v-card v-if="statusTarget">
        <v-card-title>
          <span class="text-h5">{{ statusDialogTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-alert :type="statusTarget.action === 'disable' ? 'warning' : 'info'" class="mb-0">
            <template v-if="statusTarget.type === 'user' && statusTarget.action === 'disable'">
              <strong>{{ statusTarget.item.email }}</strong> will be signed out immediately and won't be able to log in
              until they are enabled again.
            </template>
            <template v-else-if="statusTarget.type === 'user'">
              <strong>{{ statusTarget.item.email }}</strong> will be able to log in again.
            </template>
            <template v-else-if="statusTarget.action === 'disable'">
              All {{ statusTarget.item.users.length }} {{ statusTarget.item.users.length === 1 ? 'user' : 'users' }} in
              <strong>{{ statusTarget.item.name }}</strong> will be signed out immediately and won't be able to log in
              until the account is enabled again.
            </template>
            <template v-else>
              Users in <strong>{{ statusTarget.item.name }}</strong> will be able to log in again (unless they are
              disabled individually).
            </template>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="statusDialog = false">
            Cancel
          </v-btn>
          <v-btn
            :color="statusTarget.action === 'disable' ? 'red darken-1' : 'green darken-1'"
            text
            :loading="changingStatus"
            @click="performStatusChange"
          >
            {{ statusTarget.action === 'disable' ? 'Disable' : 'Enable' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification Snackbar -->
    <v-snackbar
      v-model="showNotification"
      :color="notificationColor"
      :timeout="notificationTimeout"
      top
      right
    >
      {{ notificationMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showNotification = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </main-layout>
</template>

<script>
import MainLayout from '../layouts/Main'
import Vue from 'vue'

export default {
  name: 'Admin',
  
  components: {
    MainLayout
  },
  
  created() {
    if (localStorage.sessionToken) {
      this.sessionToken = localStorage.sessionToken
      this.setDefaultBillingDates()
      this.fetchUserProfile()
    } else {
      window.location.href = '#/login'
    }
  },
  
  data: () => ({
    sessionToken: null,
    userProfile: null,
    users: [],
    loadingUsers: false,
    editUserDialog: false,
    editedUser: {
      id: null,
      email: '',
      role: ''
    },
    accounts: [],
    loadingAccounts: false,
    loadingDashboard: false,
    editAccountDialog: false,
    editingAccount: false,
    editedAccount: {
      id: null,
      name: '',
      source_platforms: '',
      destination_platforms: ''
    },
    createUserDialog: false,
    creatingUser: false,
    newUser: {
      email: '',
      password: '',
      role: 'user',
      account_id: null
    },
    resetPasswordDialog: false,
    selectedUser: null,
    newPassword: '',
    // Disable / enable users and accounts
    statusDialog: false,
    statusTarget: null,
    changingStatus: false,
    showNotification: false,
    notificationMessage: '',
    notificationColor: 'success',
    notificationTimeout: 6000,
    // Billing Reports
    reportStartDate: null,
    reportEndDate: null,
    startDateMenu: false,
    endDateMenu: false,
    generatingReport: false,
    billingReport: null,
    dashboard: {
      total_users: 0,
      total_accounts: 0,
      total_conversion_batches: 0,
      total_file_conversions: 0,
      recent_users: [],
      recent_accounts: [],
      conversion_stats: {
        pending: 0,
        processing: 0,
        completed: 0,
        failed: 0
      }
    },
    userHeaders: [
      { text: 'ID', value: 'id' },
      { text: 'Email', value: 'email' },
      { text: 'Role', value: 'role' },
      { text: 'Account ID', value: 'account_id' },
      { text: 'Created', value: 'created_at' },
      { text: 'Actions', value: 'actions', sortable: false }
    ],
    userHeadersCompact: [
      { text: 'Email', value: 'email' },
      { text: 'Role', value: 'role' },
      { text: 'Status', value: 'disabled' },
      { text: 'Created', value: 'created_at' },
      { text: 'Actions', value: 'actions', sortable: false }
    ],
    accountHeaders: [
      { text: 'ID', value: 'id' },
      { text: 'Name', value: 'name' },
      { text: 'Users', value: 'users_count' },
      { text: 'Conversions', value: 'conversion_batches_count' },
      { text: 'Source Platforms', value: 'source_platforms' },
      { text: 'Destination Platforms', value: 'destination_platforms' },
      { text: 'Created', value: 'created_at' },
      { text: 'Actions', value: 'actions', sortable: false }
    ],
    roleOptions: [
      { text: 'User', value: 'user' },
      { text: 'Admin', value: 'admin' },
      { text: 'Super Admin', value: 'super_admin' }
    ],
    summaryHeaders: [
      { text: 'Customer', value: 'Simple Customer' },
      { text: 'Company', value: 'Company Converted' },
      { text: 'Downloads', value: 'Total Downloads' },
      { text: 'Last Downloaded', value: 'Last Downloaded Date' }
    ],
    detailHeaders: [
      { text: 'Account', value: 'Account Name' },
      { text: 'Created', value: 'Created At' },
      { text: 'Conversion Key', value: 'Conversion Key' },
      { text: 'Status', value: 'Status' },
      { text: 'Downloaded', value: 'Downloaded At' },
      { text: 'Download Count', value: 'Download Count' },
      { text: 'Company', value: 'Company' },
      { text: 'Records', value: 'Records Count' }
    ],
    emailRules: [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Email must be valid'
    ],
    passwordRules: [
      v => !!v || 'Password is required',
      v => (v && v.length >= 6) || 'Password must be at least 6 characters'
    ]
  }),
  
  computed: {
    authenticated() {
      return !!this.sessionToken
    },
    userRole() {
      return this.userProfile ? this.userProfile.role : 'user'
    },
    isAdmin() {
      return this.userProfile && (this.userProfile.role === 'admin' || this.userProfile.role === 'super_admin')
    },
    accountOptions() {
      return this.accounts.map(account => ({
        id: account.id,
        name: `${account.name} (ID: ${account.id})`
      }))
    },
    isCreateUserFormValid() {
      return this.newUser.email && 
             this.newUser.password && 
             this.newUser.email.includes('@') &&
             this.newUser.password.length >= 6
    },
    accountsWithUsers() {
      return this.accounts.map(account => ({
        ...account,
        users: this.users.filter(user => user.account_id === account.id)
      }))
    },
    availableRoleOptions() {
      if (this.userRole === 'super_admin') {
        // Super admins can assign any role
        return this.roleOptions
      } else {
        // Regular admins can only assign 'user' role
        return this.roleOptions.filter(role => role.value === 'user')
      }
    },
    statusDialogTitle() {
      if (!this.statusTarget) return ''
      const verb = this.statusTarget.action === 'disable' ? 'Disable' : 'Enable'
      return `${verb} ${this.statusTarget.type === 'user' ? 'User' : 'Account'}`
    }
  },
  
  methods: {
    setDefaultBillingDates() {
      // Set to month-to-date: first day of current month to today
      const today = new Date()
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      
      // Format dates as YYYY-MM-DD for the date picker
      this.reportStartDate = firstDayOfMonth.toISOString().split('T')[0]
      this.reportEndDate = today.toISOString().split('T')[0]
    },
    
    async fetchUserProfile() {
      if (this.authenticated) {
        const url = process.env.VUE_APP_API_HOST + "/api/user-profiles/current"
        const config = {
          headers: { "Authorization": this.sessionToken }
        }

        try {
          const response = await Vue.axios.get(url, config)
          this.userProfile = response.data.object
          
          // Check if user has admin privileges
          if (!this.isAdmin) {
            window.location.href = '#/'
          } else {
            // Load all admin data after user profile is loaded
            this.loadDashboard()
            this.loadAccountsAndUsers()
          }
        } catch (e) {
          if (e.response && e.response.status == "401") {
            console.warn('User was not authenticated. Logging out.')
            this.logout()
          } else {
            console.error('Error fetching user profile:', e)
          }
        }
      }
    },
    
    async loadUsers() {
      if (!this.isAdmin) return
      
      this.loadingUsers = true
      const url = process.env.VUE_APP_API_HOST + "/api/admin/users"
      const config = {
        headers: { "Authorization": this.sessionToken }
      }

      try {
        const response = await Vue.axios.get(url, config)
        this.users = response.data.objects || []
      } catch (e) {
        console.error('Error loading users:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        }
      } finally {
        this.loadingUsers = false
      }
    },
    
    async loadDashboard() {
      if (!this.isAdmin) {
        console.warn('User is not admin, cannot load dashboard')
        return
      }
      
      this.loadingDashboard = true
      const url = process.env.VUE_APP_API_HOST + "/api/admin/dashboard"
      const config = {
        headers: { "Authorization": this.sessionToken }
      }

      try {
        console.log('Loading dashboard from:', url)
        const response = await Vue.axios.get(url, config)
        console.log('Dashboard response:', response.data)
        this.dashboard = response.data.dashboard || this.dashboard
      } catch (e) {
        console.error('Error loading dashboard:', e)
        if (e.response) {
          console.error('Response status:', e.response.status)
          console.error('Response data:', e.response.data)
          if (e.response.status === 401) {
            this.logout()
          } else {
            this.showNotificationMessage('Failed to load dashboard: ' + (e.response.data?.error || e.message), 'error')
          }
        } else {
          this.showNotificationMessage('Network error loading dashboard', 'error')
        }
      } finally {
        this.loadingDashboard = false
      }
    },
    
    editUser(user) {
      this.editedUser = {
        id: user.id,
        email: user.email,
        role: user.role
      }
      this.editUserDialog = true
    },
    
    closeEditDialog() {
      this.editUserDialog = false
      this.editedUser = {
        id: null,
        email: '',
        role: ''
      }
    },
    
    async saveUser() {
      // Role updates require super_admin role according to API docs
      if (this.userRole !== 'super_admin') {
        this.showNotificationMessage('Only Super Admins can update user roles', 'warning')
        return
      }
      
      const url = process.env.VUE_APP_API_HOST + `/api/admin/users/${this.editedUser.id}/update_role`
      const config = {
        headers: { "Authorization": this.sessionToken }
      }

      try {
        const response = await Vue.axios.patch(url, {
          role: this.editedUser.role
        }, config)
        
        // Update the user in the local list
        const userIndex = this.users.findIndex(u => u.id === this.editedUser.id)
        if (userIndex !== -1) {
          this.users[userIndex].role = response.data.object.role
        }
        
        this.closeEditDialog()
        
        this.showNotificationMessage('User role updated successfully', 'success')
      } catch (e) {
        console.error('Error updating user role:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        } else if (e.response && e.response.status === 403) {
          this.showNotificationMessage('You are not authorized to perform this action', 'error')
        } else {
          this.showNotificationMessage('Failed to update user role', 'error')
        }
      }
    },
    
    getRoleColor(role) {
      switch (role) {
        case 'super_admin':
          return 'red'
        case 'admin':
          return 'orange'
        default:
          return 'blue'
      }
    },
    
    async loadAccounts() {
      if (!this.isAdmin) return
      
      this.loadingAccounts = true
      const url = process.env.VUE_APP_API_HOST + "/api/admin/accounts"
      const config = {
        headers: { "Authorization": this.sessionToken }
      }

      try {
        const response = await Vue.axios.get(url, config)
        this.accounts = response.data.objects || []
      } catch (e) {
        console.error('Error loading accounts:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        }
      } finally {
        this.loadingAccounts = false
      }
    },
    
    showCreateAccountDialog() {
      this.editingAccount = false
      this.editedAccount = {
        id: null,
        name: '',
        source_platforms: '',
        destination_platforms: ''
      }
      this.editAccountDialog = true
    },
    
    editAccount(account) {
      this.editingAccount = true
      this.editedAccount = {
        id: account.id,
        name: account.name,
        source_platforms: account.source_platforms || '',
        destination_platforms: account.destination_platforms || ''
      }
      this.editAccountDialog = true
    },
    
    closeAccountDialog() {
      this.editAccountDialog = false
      this.editingAccount = false
      this.editedAccount = {
        id: null,
        name: '',
        source_platforms: '',
        destination_platforms: ''
      }
    },
    
    async saveAccount() {
      const isCreate = !this.editingAccount
      const url = isCreate 
        ? process.env.VUE_APP_API_HOST + "/api/admin/accounts"
        : process.env.VUE_APP_API_HOST + `/api/admin/accounts/${this.editedAccount.id}`
      
      const config = {
        headers: { "Authorization": this.sessionToken }
      }
      
      const data = {
        account: {
          name: this.editedAccount.name,
          source_platforms: this.editedAccount.source_platforms,
          destination_platforms: this.editedAccount.destination_platforms
        }
      }

      try {
        const response = isCreate 
          ? await Vue.axios.post(url, data, config)
          : await Vue.axios.patch(url, data, config)
        
        if (isCreate) {
          this.accounts.push(response.data.object)
        } else {
          // Update the account in the local list
          const accountIndex = this.accounts.findIndex(a => a.id === this.editedAccount.id)
          if (accountIndex !== -1) {
            this.accounts[accountIndex] = response.data.object
          }
        }
        
        this.closeAccountDialog()
        this.showNotificationMessage(`Account ${isCreate ? 'created' : 'updated'} successfully`, 'success')
      } catch (e) {
        console.error('Error saving account:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        } else if (e.response && e.response.status === 403) {
          this.showNotificationMessage('You are not authorized to perform this action', 'error')
        } else if (e.response && e.response.data && e.response.data.errors) {
          this.showNotificationMessage('Error: ' + e.response.data.errors.join(', '), 'error')
        } else {
          this.showNotificationMessage(`Failed to ${isCreate ? 'create' : 'update'} account`, 'error')
        }
      }
    },
    
    
    loadAccountsAndUsers() {
      // Load both accounts and users simultaneously
      Promise.all([
        this.loadAccounts(),
        this.loadUsers()
      ])
    },
    
    showCreateUserDialog(accountId = null) {
      this.newUser = {
        email: '',
        password: '',
        role: 'user', // Always default to 'user' role
        account_id: accountId
      }
      this.createUserDialog = true
    },
    
    closeCreateUserDialog() {
      this.createUserDialog = false
      this.newUser = {
        email: '',
        password: '',
        role: 'user',
        account_id: null
      }
    },
    
    async createUser() {
      if (!this.isCreateUserFormValid) {
        this.showNotificationMessage('Please fill in all required fields correctly', 'warning')
        return
      }
      
      // Additional validation: Regular admins can only create 'user' role
      if (this.userRole !== 'super_admin' && this.newUser.role !== 'user') {
        this.showNotificationMessage('Regular Admins can only create users with "user" role', 'warning')
        return
      }
      
      this.creatingUser = true
      const url = process.env.VUE_APP_API_HOST + "/api/admin/users"
      const config = {
        headers: { "Authorization": this.sessionToken }
      }
      
      // Prepare the request data according to API spec
      const userData = {
        email: this.newUser.email,
        password: this.newUser.password
      }
      
      // Add optional fields if specified
      if (this.newUser.role && this.newUser.role !== 'user') {
        userData.role = this.newUser.role
      }
      
      // Super Admin can specify account_id, Admin users are created in their own account
      if (this.userRole === 'super_admin' && this.newUser.account_id) {
        userData.account_id = this.newUser.account_id
      }
      
      const requestData = { user: userData }

      try {
        console.log('Creating user:', requestData)
        const response = await Vue.axios.post(url, requestData, config)
        console.log('User created:', response.data)
        
        // Add the new user to the local list
        this.users.push(response.data.object)
        
        this.closeCreateUserDialog()
        this.showNotificationMessage(`User "${response.data.object.email}" created successfully!`, 'success')
      } catch (e) {
        console.error('Error creating user:', e)
        if (e.response) {
          console.error('Response status:', e.response.status)
          console.error('Response data:', e.response.data)
          if (e.response.status === 401) {
            this.logout()
          } else if (e.response.status === 403) {
            this.showNotificationMessage('You are not authorized to create users', 'error')
          } else if (e.response.status === 422 && e.response.data.errors) {
            this.showNotificationMessage('Validation errors: ' + e.response.data.errors.join(', '), 'error')
          } else {
            this.showNotificationMessage('Failed to create user: ' + (e.response.data?.error || e.message), 'error')
          }
        } else {
          this.showNotificationMessage('Network error creating user', 'error')
        }
      } finally {
        this.creatingUser = false
      }
    },
    
    resetPassword(user) {
      this.selectedUser = user
      this.newPassword = ''
      this.resetPasswordDialog = true
    },
    
    closeResetPasswordDialog() {
      this.resetPasswordDialog = false
      this.selectedUser = null
      this.newPassword = ''
    },
    
    confirmPasswordReset() {
      if (!this.newPassword || this.newPassword.length < 6) {
        this.showNotificationMessage('Password must be at least 6 characters long', 'warning')
        return
      }
      
      this.performPasswordReset(this.selectedUser, this.newPassword)
    },
    
    async performPasswordReset(user, password) {
      const url = process.env.VUE_APP_API_HOST + `/api/admin/users/${user.id}/reset_password`
      const config = {
        headers: { "Authorization": this.sessionToken }
      }
      
      try {
        await Vue.axios.patch(url, { password }, config)
        this.closeResetPasswordDialog()
        this.showNotificationMessage(`Password reset successfully for ${user.email}`, 'success')
      } catch (e) {
        console.error('Error resetting password:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        } else if (e.response && e.response.status === 403) {
          this.showNotificationMessage('You are not authorized to reset passwords for this user', 'error')
        } else if (e.response && e.response.data && e.response.data.errors) {
          this.showNotificationMessage('Error: ' + e.response.data.errors.join(', '), 'error')
        } else {
          this.showNotificationMessage('Failed to reset password', 'error')
        }
      }
    },
    
    isAdminRole(role) {
      return role === 'admin' || role === 'super_admin'
    },

    // Mirrors the API rules: nobody can change their own status, and only super
    // admins can disable/enable admins (or accounts that contain admins).
    canChangeUserStatus(user) {
      if (this.userProfile && user.id === this.userProfile.id) return false
      return this.userRole === 'super_admin' || !this.isAdminRole(user.role)
    },

    canChangeAccountStatus(account) {
      if (this.userProfile && account.id === this.userProfile.account_id) return false
      return this.userRole === 'super_admin' || !account.users.some(user => this.isAdminRole(user.role))
    },

    confirmStatusChange(type, item) {
      this.statusTarget = { type, item, action: item.disabled ? 'enable' : 'disable' }
      this.statusDialog = true
    },

    async performStatusChange() {
      const { type, item, action } = this.statusTarget
      const resource = type === 'user' ? 'users' : 'accounts'
      const url = process.env.VUE_APP_API_HOST + `/api/admin/${resource}/${item.id}/${action}`
      const config = {
        headers: { "Authorization": this.sessionToken }
      }

      this.changingStatus = true
      try {
        const response = await Vue.axios.patch(url, {}, config)

        // splice so Vue picks up the replaced item
        const list = type === 'user' ? this.users : this.accounts
        const index = list.findIndex(i => i.id === item.id)
        if (index !== -1) {
          list.splice(index, 1, response.data.object)
        }

        this.statusDialog = false
        const label = type === 'user' ? item.email : item.name
        this.showNotificationMessage(`${label} ${action === 'disable' ? 'disabled' : 'enabled'}`, 'success')
      } catch (e) {
        console.error(`Error trying to ${action} ${type}:`, e)
        if (e.response && e.response.status === 401) {
          this.logout()
        } else if (e.response && e.response.data && e.response.data.error) {
          this.showNotificationMessage(e.response.data.error, 'error')
        } else if (e.response && e.response.data && e.response.data.errors) {
          this.showNotificationMessage('Error: ' + e.response.data.errors.join(', '), 'error')
        } else {
          this.showNotificationMessage(`Failed to ${action} ${type}`, 'error')
        }
      } finally {
        this.changingStatus = false
      }
    },

    async generateBillingReport() {
      if (!this.reportStartDate || !this.reportEndDate) {
        this.showNotificationMessage('Please select both start and end dates', 'warning')
        return
      }
      
      this.generatingReport = true
      const url = process.env.VUE_APP_API_HOST + "/api/admin/billing_reports/generate"
      const config = {
        headers: { "Authorization": this.sessionToken }
      }
      
      const requestData = {
        start_date: this.reportStartDate,
        end_date: this.reportEndDate
      }

      try {
        console.log('Generating billing report:', requestData)
        const response = await Vue.axios.post(url, requestData, config)
        console.log('Billing report response:', response.data)
        
        this.billingReport = response.data.report
        this.showNotificationMessage('Billing report generated successfully', 'success')
      } catch (e) {
        console.error('Error generating billing report:', e)
        if (e.response && e.response.status === 401) {
          this.logout()
        } else if (e.response && e.response.status === 400) {
          this.showNotificationMessage(e.response.data.error || 'Invalid date range', 'error')
        } else if (e.response && e.response.status === 403) {
          this.showNotificationMessage('You are not authorized to generate billing reports', 'error')
        } else {
          this.showNotificationMessage('Failed to generate billing report', 'error')
        }
      } finally {
        this.generatingReport = false
      }
    },
    
    async downloadDetailCSV() {
      if (!this.reportStartDate || !this.reportEndDate) {
        this.showNotificationMessage('Please select both start and end dates', 'warning')
        return
      }
      
      try {
        const url = process.env.VUE_APP_API_HOST + `/api/admin/billing_reports/download_detail_csv?start_date=${this.reportStartDate}&end_date=${this.reportEndDate}`
        const config = {
          headers: { "Authorization": this.sessionToken },
          responseType: 'blob'
        }
        
        const response = await Vue.axios.get(url, config)
        
        // Create blob and download link
        const blob = new Blob([response.data], { type: 'text/csv' })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `billing_report_detail_${this.reportStartDate.replace(/-/g, '')}_${this.reportEndDate.replace(/-/g, '')}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
        
        this.showNotificationMessage('Detail CSV downloaded successfully', 'success')
      } catch (e) {
        console.error('Error downloading detail CSV:', e)
        if (e.response?.status === 401) {
          this.logout()
        } else {
          this.showNotificationMessage('Failed to download detail CSV: ' + (e.response?.data?.error || e.message), 'error')
        }
      }
    },
    
    async downloadSummaryCSV() {
      if (!this.reportStartDate || !this.reportEndDate) {
        this.showNotificationMessage('Please select both start and end dates', 'warning')
        return
      }
      
      try {
        const url = process.env.VUE_APP_API_HOST + `/api/admin/billing_reports/download_summary_csv?start_date=${this.reportStartDate}&end_date=${this.reportEndDate}`
        const config = {
          headers: { "Authorization": this.sessionToken },
          responseType: 'blob'
        }
        
        const response = await Vue.axios.get(url, config)
        
        // Create blob and download link
        const blob = new Blob([response.data], { type: 'text/csv' })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `billing_report_summary_${this.reportStartDate.replace(/-/g, '')}_${this.reportEndDate.replace(/-/g, '')}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)
        
        this.showNotificationMessage('Summary CSV downloaded successfully', 'success')
      } catch (e) {
        console.error('Error downloading summary CSV:', e)
        if (e.response?.status === 401) {
          this.logout()
        } else {
          this.showNotificationMessage('Failed to download summary CSV: ' + (e.response?.data?.error || e.message), 'error')
        }
      }
    },
    
    showNotificationMessage(message, color = 'success', timeout = 6000) {
      this.notificationMessage = message
      this.notificationColor = color
      this.notificationTimeout = timeout
      this.showNotification = true
    },
    
    getAccountName(accountId) {
      const account = this.accounts.find(a => a.id === accountId)
      return account ? account.name : `Account ${accountId}`
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },
    
    logout() {
      this.sessionToken = null
      localStorage.sessionToken = null
      window.location.href = '#/login'
    }
  }
}
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}
</style>
